import React, { useState } from 'react';
import {
    Box,
    Popover,
    Typography,
    Button,
    useTheme,
    Divider,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    IconButton
} from '@mui/material';
import { FilterList, Close } from '@mui/icons-material';
import { BasicAutocomplete } from './BasicAutocomplete';

export interface AuditFilterConfig {
    activityTypes: string[];
    deviceTypes: string[];
    userType: 'all' | 'authenticated' | 'anonymous';
    dateRange: 'all' | '1h' | '6h' | '24h' | '7d' | '30d';
    screens: string[];
}

interface AuditFiltersProps {
    filters: AuditFilterConfig;
    onChange: (filters: AuditFilterConfig) => void;
    availableActivityTypes: string[];
    availableDeviceTypes: string[];
    availableScreens: string[];
}

const defaultFilters: AuditFilterConfig = {
    activityTypes: [],
    deviceTypes: [],
    userType: 'all',
    dateRange: 'all',
    screens: []
};

export const AuditFilters: React.FC<AuditFiltersProps> = ({
    filters,
    onChange,
    availableActivityTypes,
    availableDeviceTypes,
    availableScreens
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    // Ensure filters has default values if undefined
    const safeFilters = filters || defaultFilters;
    const [tempFilters, setTempFilters] = useState<AuditFilterConfig>(safeFilters);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTempFilters(safeFilters);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        onChange(tempFilters);
        handleClose();
    };

    const handleReset = () => {
        setTempFilters(defaultFilters);
        onChange(defaultFilters);
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (safeFilters.activityTypes?.length > 0) count++;
        if (safeFilters.deviceTypes?.length > 0) count++;
        if (safeFilters.userType !== 'all') count++;
        if (safeFilters.dateRange !== 'all') count++;
        if (safeFilters.screens?.length > 0) count++;
        return count;
    };

    const activeCount = getActiveFilterCount();
    const open = Boolean(anchorEl);

    return (
        <>
            <Button
                onClick={handleClick}
                size='small'
                endIcon={<FilterList fontSize='small' />}
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? activeCount > 0
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(255, 255, 255, 0.08)'
                        : activeCount > 0
                            ? 'rgba(0, 0, 0, 0.12)'
                            : 'rgba(0, 0, 0, 0.04)',
                    color: theme.palette.text.primary,
                    fontSize: '0.75rem',
                    fontFamily: 'primaryFont',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    borderRadius: 1.5,
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(0, 0, 0, 0.12)',
                    },
                    position: 'relative'
                }}
            >
                Advanced Filter
                {activeCount > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.9)'
                                : theme.palette.text.primary,
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography sx={{
                            fontSize: '0.6rem',
                            color: theme.palette.mode === 'dark'
                                ? theme.palette.background.paper
                                : 'white',
                            fontWeight: 'bold'
                        }}>
                            {activeCount}
                        </Typography>
                    </Box>
                )}
            </Button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { 
                        width: 420, 
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                {/* Sticky Header with Actions */}
                <Box sx={{ 
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: 3,
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterList
                                fontSize='small'
                                sx={{ color: theme.palette.text.secondary }}
                            />
                            <Typography variant='subtitle1' sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                Audit Log Filters
                            </Typography>
                        </Box>
                        
                        {/* Action Buttons - Top Right */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                onClick={handleReset}
                                size='small'
                                variant='contained'
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontFamily: 'primaryFont',
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.08)',
                                    color: theme.palette.text.primary,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.12)'
                                            : 'rgba(0, 0, 0, 0.12)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Reset
                            </Button>
                            <Button
                                onClick={handleApply}
                                size='small'
                                variant='contained'
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontFamily: 'primaryFont'
                                }}
                            >
                                Apply
                            </Button>
                            <IconButton size='small' onClick={handleClose} sx={{ ml: 0.5 }}>
                                <Close fontSize='small' />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{ 
                    overflowY: 'auto', 
                    p: 3,
                    flex: 1
                }}>
                    {/* Date Range */}
                    <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                        <FormLabel component='legend' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                            Time Range
                        </FormLabel>
                        <RadioGroup
                            value={tempFilters.dateRange}
                            onChange={(e) => setTempFilters({ ...tempFilters, dateRange: e.target.value as any })}
                        >
                            <FormControlLabel
                                value='all'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='All Time'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='1h'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Last Hour'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='6h'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Last 6 Hours'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='24h'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Last 24 Hours'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='7d'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Last 7 Days'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='30d'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Last 30 Days'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Divider sx={{ mb: 3 }} />

                    {/* User Type */}
                    <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                        <FormLabel component='legend' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                            User Type
                        </FormLabel>
                        <RadioGroup
                            value={tempFilters.userType}
                            onChange={(e) => setTempFilters({ ...tempFilters, userType: e.target.value as any })}
                        >
                            <FormControlLabel
                                value='all'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='All Users'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='authenticated'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Authenticated Only'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='anonymous'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Anonymous Only'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Divider sx={{ mb: 3 }} />

                    {/* Activity Types */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Activity Types
                        </Typography>
                        <BasicAutocomplete
                            multiple
                            name='activityTypes'
                            label='Select Activity Types'
                            fullWidth
                            size='small'
                            options={availableActivityTypes}
                            value={tempFilters.activityTypes}
                            onChange={(newValue) => setTempFilters({ ...tempFilters, activityTypes: Array.isArray(newValue) ? newValue : [] })}
                        />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Device Types */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Device Types
                        </Typography>
                        <BasicAutocomplete
                            multiple
                            name='deviceTypes'
                            label='Select Device Types'
                            fullWidth
                            size='small'
                            options={availableDeviceTypes}
                            value={tempFilters.deviceTypes}
                            onChange={(newValue) => setTempFilters({ ...tempFilters, deviceTypes: Array.isArray(newValue) ? newValue : [] })}
                        />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Screens */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Screens
                        </Typography>
                        <BasicAutocomplete
                            multiple
                            name='screens'
                            label='Select Screens'
                            fullWidth
                            size='small'
                            options={availableScreens}
                            value={tempFilters.screens}
                            onChange={(newValue) => setTempFilters({ ...tempFilters, screens: Array.isArray(newValue) ? newValue : [] })}
                        />
                    </Box>

                    {/* Active Filters Summary */}
                    {activeCount > 0 && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                    Active Filters ({activeCount})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {safeFilters.dateRange !== 'all' && (
                                        <Chip
                                            label={`Time: ${safeFilters.dateRange}`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, dateRange: 'all' })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {safeFilters.userType !== 'all' && (
                                        <Chip
                                            label={`User: ${safeFilters.userType}`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, userType: 'all' })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {safeFilters.activityTypes?.length > 0 && (
                                        <Chip
                                            label={`${safeFilters.activityTypes.length} activity types`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, activityTypes: [] })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {safeFilters.deviceTypes?.length > 0 && (
                                        <Chip
                                            label={`${safeFilters.deviceTypes.length} device types`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, deviceTypes: [] })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {safeFilters.screens?.length > 0 && (
                                        <Chip
                                            label={`${safeFilters.screens.length} screens`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, screens: [] })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
};

